import React, { useMemo, useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import Icons from '../components/Icons';
import Avatar from '../components/ui/Avatar';
import AccessibilitySettingsDialog from '../components/AccessibilitySettingsDialog';
import BroadcastService from '../services/BroadcastService';
import BroadcastModal from '../components/broadcast/BroadcastModal';
import BroadcastBanner from '../components/broadcast/BroadcastBanner';
import RoleSettingsService from '../services/RoleSettingsService';
import { useUser } from '../store/UserContext';
import { useStudentStore } from '../store/StudentStore';
import { useSettings } from '../store/SettingsContext';
import { useDisplaySettings } from '../store/DisplaySettingsContext';
import { useFeaturePacks } from '../store/FeaturePackContext';
import { MODULE_PACK_MAP } from '../config/featurePacks';
import { formatLongDate } from '../utils/dateFormat';

const matchesPath = (path, currentPath) => {
  if (!path) return false;
  if (path.includes('?')) return currentPath.startsWith(path);
  return currentPath === path || currentPath.startsWith(`${path}/`);
};

export default function AppShell() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { user, signOut } = useUser();
  const { students } = useStudentStore();
  const { settings } = useSettings();
  const { settings: displaySettings, setSettings: setDisplaySettings } = useDisplaySettings();
  const { packs } = useFeaturePacks();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [accessibilityOpen, setAccessibilityOpen] = useState(false);
  const broadcastService = useMemo(() => new BroadcastService(), []);
  const roleService = useMemo(() => new RoleSettingsService(), []);
  const [activeBroadcast, setActiveBroadcast] = useState(null);
  const [, setRoleRevision] = useState(0);

  const criticalCount = useMemo(
    () => students.filter((s) => s.riskLevel === 'critical').length,
    [students]
  );

  const rolePermissions = user
    ? roleService.getPermissions(user.roleKey || (user.accessScope === 'admin' ? 'admin' : 'teacher'))
    : {};
  const canInternalMessages = Boolean(rolePermissions.messages);
  const canExternalMessages = Boolean(rolePermissions['external-messages']);

  const currentPath = `${location.pathname}${location.search}`;

  const navSections = useMemo(() => ([
    {
      id: 'main',
      label: 'Main Menu',
      items: [
        { id: 'dashboard', label: 'Dashboard', icon: Icons.Dashboard, path: '/dashboard', description: 'Daily summary and key alerts' }
      ]
    },
    {
      id: 'people',
      label: 'People & Casework',
      items: [
        {
          id: 'students',
          label: 'Students',
          icon: Icons.Students,
          path: '/students',
          description: 'Profiles, reviews and compliance',
          badge: students.length,
          children: [
            { id: 'students-all', permissionId: 'students', label: 'All students', path: '/students' },
            { id: 'students-new', permissionId: 'students', label: 'Add student', path: '/students/enrol' },
            { id: 'students-review', permissionId: 'students', label: 'Under review', path: '/students?view=in-review' },
            { id: 'compliance', permissionId: 'compliance', label: 'Compliance', path: '/students?view=compliance' }
          ]
        },
        {
          id: 'support-workers',
          label: 'Support Workers',
          icon: Icons.Users,
          path: '/support-workers',
          description: 'Staff records and access control',
          children: [
            { id: 'support-workers-list', permissionId: 'support-workers', label: 'Team directory', path: '/support-workers' },
            { id: 'support-workers-new', permissionId: 'support-workers', label: 'Add support worker', path: '/support-workers/new' }
          ]
        },
        {
          id: 'safeguarding-hub',
          permissionId: 'safeguarding',
          label: 'Safeguarding & Incidents',
          icon: Icons.Shield,
          path: '/students?view=safeguarding',
          description: 'Risks, concerns and incident logging',
          badge: criticalCount || null,
          children: [
            { id: 'safeguarding', permissionId: 'safeguarding', label: 'Safeguarding view', path: '/students?view=safeguarding' },
            { id: 'safeguarding-new', permissionId: 'safeguarding', label: 'Log concern', path: '/safeguarding/new' },
            { id: 'incidents', permissionId: 'incidents', label: 'Incidents', path: '/incidents' },
            { id: 'incidents-new', permissionId: 'incidents', label: 'New incident', path: '/incidents/new' }
          ]
        }
      ]
    },
    {
      id: 'communications',
      label: 'Communication',
      items: [
        { id: 'broadcasts', label: 'Announcements', icon: Icons.Bell, path: '/broadcasts', description: 'Broadcasts and centre-wide updates' },
        {
          id: 'messages-hub',
          label: 'Messages',
          icon: Icons.Messages,
          path: canInternalMessages ? '/messages' : '/messages/external',
          description: 'Internal and external conversations',
          children: [
            { id: 'messages', permissionId: 'messages', label: 'Internal messages', path: '/messages' },
            { id: 'external-messages', permissionId: 'external-messages', label: 'External messages', path: '/messages/external' }
          ]
        }
      ]
    },
    {
      id: 'operations',
      label: 'Operations',
      items: [
        {
          id: 'programmes',
          label: 'Activities & Programmes',
          icon: Icons.Programmes,
          path: '/programmes',
          description: 'Schedules, groups and lesson planning',
          children: [
            { id: 'programmes-calendar', permissionId: 'programmes', label: 'Calendar view', path: '/programmes' },
            { id: 'programmes-overview', permissionId: 'programmes', label: 'Programme overview', path: '/programmes/overview' },
            { id: 'programmes-new', permissionId: 'programmes', label: 'Add programme', path: '/programmes/overview/new' },
            { id: 'activities-new', permissionId: 'programmes', label: 'Add activity', path: '/activities/new' }
          ]
        },
        {
          id: 'inventory',
          label: 'Inventory',
          icon: Icons.Inventory,
          path: '/inventory',
          description: 'Assets, devices and allocations',
          children: [
            { id: 'inventory-list', permissionId: 'inventory', label: 'Inventory list', path: '/inventory' },
            { id: 'inventory-new', permissionId: 'inventory', label: 'Add inventory item', path: '/inventory/new' }
          ]
        },
        { id: 'attendance', label: 'Attendance', icon: Icons.Clock, path: '/attendance', description: 'Registers, logs and follow-up' }
      ]
    },
    {
      id: 'admin',
      label: 'Insights & Admin',
      items: [
        { id: 'reports', label: 'Reports', icon: Icons.Reports, path: '/reports', description: 'Exports, KPIs and audits' },
        { id: 'settings', label: 'Settings', icon: Icons.Settings, path: '/settings', description: 'System setup and templates' }
      ]
    }
  ]), [canInternalMessages, criticalCount, students.length]);

  const filterNavItem = (item) => {
    const visibleChildren = (item.children || [])
      .map((child) => filterNavItem(child))
      .filter(Boolean);
    const permissionId = item.permissionId || item.id;
    const hasPermission = permissionId ? Boolean(rolePermissions[permissionId]) : true;
    const packId = permissionId ? MODULE_PACK_MAP[permissionId] : null;
    const packAllowed = !packId || packs?.[packId] !== false;

    if (visibleChildren.length > 0) {
      return {
        ...item,
        path: item.path || visibleChildren[0].path,
        children: visibleChildren
      };
    }

    if (!hasPermission || !packAllowed) return null;
    return { ...item };
  };

  const visibleSections = navSections
    .map((section) => ({
      ...section,
      items: section.items.map((item) => filterNavItem(item)).filter(Boolean)
    }))
    .filter((section) => section.items.length > 0);

  const flatNavItems = visibleSections.flatMap((section) =>
    section.items.flatMap((item) => [item, ...(item.children || [])])
  );

  const activeParentIds = visibleSections.flatMap((section) =>
    section.items
      .filter((item) => (item.children || []).some((child) => matchesPath(child.path, currentPath)))
      .map((item) => item.id)
  );

  const [expandedGroups, setExpandedGroups] = useState(() =>
    Object.fromEntries(activeParentIds.map((id) => [id, true]))
  );

  useEffect(() => {
    if (!activeParentIds.length) return;
    setExpandedGroups((prev) => {
      const next = { ...prev };
      let changed = false;
      activeParentIds.forEach((id) => {
        if (!next[id]) {
          next[id] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [activeParentIds]);

  const currentLabel =
    [...flatNavItems]
      .sort((a, b) => b.path.length - a.path.length)
      .find((item) => matchesPath(item.path, currentPath))?.label ||
    (location.pathname.startsWith('/safeguarding') ? 'Safeguarding' : 'Dashboard');

  const todayLabel = formatLongDate(new Date());
  const siteName = settings?.siteName || 'SafeguardPro';
  const siteSubtitle = settings?.siteSubtitle || 'Youth Centre Management';

  useEffect(() => {
    if (!user) return undefined;
    const refresh = () => {
      setActiveBroadcast(broadcastService.getActiveForUser(user.id));
    };
    refresh();
    const interval = window.setInterval(refresh, 60000);
    window.addEventListener('storage', refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('storage', refresh);
    };
  }, [broadcastService, user]);

  useEffect(() => {
    const handleRolesUpdated = () => setRoleRevision((prev) => prev + 1);
    window.addEventListener('roles:updated', handleRolesUpdated);
    return () => window.removeEventListener('roles:updated', handleRolesUpdated);
  }, []);

  const handleAcknowledgeBroadcast = (broadcast) => {
    if (!broadcast || !user) return;
    broadcastService.acknowledge(broadcast, user.id);
    setActiveBroadcast(broadcastService.getActiveForUser(user.id));
  };

  return (
    <div className="sp-shell">
      <a href="#main-content" className="sp-skip-link">Skip to content</a>
      <aside
        className={`${
          sidebarOpen ? 'w-64' : 'w-20'
        } sp-sidebar flex flex-col transition-all duration-300 fixed h-full z-40`}
        style={{ borderRightColor: 'var(--sp-border)' }}
      >
        <div className="p-4 border-b" style={{ borderBottomColor: 'var(--sp-border)' }}>
          <div className="flex items-center gap-3">
            {settings?.siteLogo ? (
              <div className="sp-panel sp-panel--muted w-10 h-10 rounded-full flex items-center justify-center overflow-hidden">
                <img src={settings.siteLogo} alt={`${siteName} logo`} className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="sp-panel sp-panel--muted w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold">
                {(siteName || 'SP').slice(0, 2).toUpperCase()}
              </div>
            )}
            {sidebarOpen && (
              <div>
                <h1 className="sp-value" style={{ fontWeight: 600 }}>{siteName}</h1>
                <p className="text-xs sp-muted">{siteSubtitle}</p>
              </div>
            )}
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-4 overflow-y-auto">
          {visibleSections.map((section) => (
            <div key={section.id} className="sp-nav-section">
              {sidebarOpen && <div className="sp-nav-section__label">{section.label}</div>}
              <div className="space-y-2">
                {section.items.map((item) => {
                  const itemActive = matchesPath(item.path, currentPath)
                    || (item.children || []).some((child) => matchesPath(child.path, currentPath));
                  const isExpanded = sidebarOpen && (expandedGroups[item.id] ?? false);
                  return (
                    <div key={item.id} className="sp-nav-group">
                      <div className="sp-nav-row">
                        <NavLink
                          to={item.path}
                          end={item.path === '/dashboard'}
                          className={`sp-nav-link ${itemActive ? 'sp-nav-link--active' : ''} ${isExpanded ? 'sp-nav-link--expanded' : ''}`}
                        >
                          {item.icon ? <item.icon /> : <span className="sp-nav-dot" />}
                          {sidebarOpen && (
                            <>
                              <span className="sp-nav-link__labelwrap">
                                <span>{item.label}</span>
                                {item.description && <span className="sp-nav-link__meta">{item.description}</span>}
                              </span>
                              {item.badge ? (
                                <span className={`sp-nav-chip ${item.id === 'safeguarding-hub' ? 'sp-nav-chip--critical' : ''}`}>
                                  {item.badge}
                                </span>
                              ) : null}
                            </>
                          )}
                        </NavLink>
                        {sidebarOpen && item.children?.length ? (
                          <button
                            type="button"
                            className="sp-nav-toggle"
                            aria-label={isExpanded ? `Collapse ${item.label}` : `Expand ${item.label}`}
                            onClick={() => setExpandedGroups((prev) => ({ ...prev, [item.id]: !isExpanded }))}
                          >
                            <Icons.ChevronRight />
                          </button>
                        ) : null}
                      </div>
                      {sidebarOpen && item.children?.length && isExpanded ? (
                        <div className="sp-nav-children">
                          {item.children.map((child) => (
                            <NavLink
                              key={child.id}
                              to={child.path}
                              end={child.path === '/students' || child.path === '/support-workers' || child.path === '/inventory' || child.path === '/incidents' || child.path === '/messages' || child.path === '/programmes' || child.path === '/programmes/overview'}
                              className={`sp-nav-link sp-nav-link--child ${matchesPath(child.path, currentPath) ? 'sp-nav-link--active' : ''}`}
                            >
                              <span className="sp-nav-dot" />
                              <span>{child.label}</span>
                            </NavLink>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-4 border-t" style={{ borderTopColor: 'var(--sp-border)' }}>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="sp-button sp-button--secondary w-full"
          >
            {sidebarOpen ? 'Collapse' : 'Expand'}
          </button>
        </div>
      </aside>

      <main id="main-content" tabIndex={-1} className={`flex-1 ${sidebarOpen ? 'ml-64' : 'ml-20'} transition-all duration-300`}>
        <header className="sp-header px-6 py-4 sticky top-0 z-30">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h2 className="sp-value" style={{ fontSize: '18px', fontWeight: 600 }}>
                {currentLabel || 'Safeguarding Dashboard'}
              </h2>
              <p className="text-sm sp-muted">{todayLabel}</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="sp-segment" role="radiogroup" aria-label="Theme mode">
                {['auto', 'light', 'dark'].map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    role="radio"
                    aria-checked={displaySettings.theme === mode}
                    className={displaySettings.theme === mode ? 'active' : ''}
                    onClick={() => setDisplaySettings((prev) => ({ ...prev, theme: mode }))}
                  >
                    {mode.charAt(0).toUpperCase() + mode.slice(1)}
                  </button>
                ))}
              </div>
              <button
                className="sp-button sp-button--ghost"
                type="button"
                aria-label="Open accessibility and display settings"
                onClick={() => setAccessibilityOpen(true)}
              >
                <Icons.Settings />
              </button>
              <button className="sp-button sp-button--ghost relative" type="button">
                <Icons.Bell />
                {criticalCount > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 rounded-full" style={{ background: 'var(--sp-red)' }} />
                )}
              </button>
              <div className="relative">
                <button
                  type="button"
                  className="flex items-center gap-3"
                  onClick={() => setMenuOpen((prev) => !prev)}
                >
                  <Avatar name={user.name} size="sm" />
                  <div className="hidden md:block text-left">
                    <p className="text-sm" style={{ fontWeight: 600 }}>{user.name}</p>
                    <p className="text-xs sp-muted">{user.title}</p>
                  </div>
                </button>
                {menuOpen && (
                  <div className="sp-panel sp-panel--muted absolute right-0 mt-2 w-48 p-2 z-40">
                    <button
                      type="button"
                      className="sp-button sp-button--ghost w-full text-left"
                      onClick={() => {
                        setMenuOpen(false);
                        navigate('/profile');
                      }}
                    >
                      My profile
                    </button>
                    {canInternalMessages && (
                      <button
                        type="button"
                        className="sp-button sp-button--ghost w-full text-left"
                        onClick={() => {
                          setMenuOpen(false);
                          navigate('/messages');
                        }}
                      >
                        Internal messages
                      </button>
                    )}
                    {canExternalMessages && (
                      <button
                        type="button"
                        className="sp-button sp-button--ghost w-full text-left"
                        onClick={() => {
                          setMenuOpen(false);
                          navigate('/messages/external');
                        }}
                      >
                        External messages
                      </button>
                    )}
                    <button
                      type="button"
                      className="sp-button sp-button--ghost w-full text-left"
                      onClick={() => {
                        setMenuOpen(false);
                        signOut();
                        navigate('/login');
                      }}
                    >
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <div className="p-6">
          {activeBroadcast && (
            <div className="mb-4">
              <BroadcastBanner
                broadcast={activeBroadcast}
                onReview={() => setActiveBroadcast(broadcastService.getActiveForUser(user.id))}
              />
            </div>
          )}
          {user?.passwordMustChange && (
            <div className="sp-alert sp-alert--warning text-sm mb-4">
              This is a seeded account. Please update your password in your profile.
            </div>
          )}
          <Outlet />
        </div>
      </main>
      <AccessibilitySettingsDialog isOpen={accessibilityOpen} onClose={() => setAccessibilityOpen(false)} />
      {activeBroadcast?.ack_required && (
        <BroadcastModal broadcast={activeBroadcast} onAcknowledge={handleAcknowledgeBroadcast} />
      )}
    </div>
  );
}
